import React, { useState, useEffect } from 'react';
import omit from 'lodash/omit';
import {
	Button,
	CheckboxInput,
	Input,
	Typography,
	SideModal,
} from 'design_system/src';
import { Company } from 'models/companies/interfaces/companies.interface';
import {
	CompanyEditorForm,
	CreateCompanyDto,
	UpdateCompanyDto,
} from 'models/user-management/interfaces/company-management.interface';
import {
	createCompany,
	updateCompany,
	fetchCities,
} from 'models/user-management/controllers/companies.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import {
	CompanyRole,
	CompanyStatus,
} from 'models/companies/enums/companies.enums';
import { City } from 'models/user-management/interfaces/city.interface';

interface EditCompanyProps {
	company?: Company;
	onClose: () => void;
	onSave: () => void;
}

function EditCompany({ company, onClose, onSave }: EditCompanyProps) {
	const [open, setOpen] = useState(true);
	const [formData, setFormData] = useState<CompanyEditorForm>({
		name: '',
		email: '',
		phone: '',
		taxId: '',
		city: '',
		tileAccount: '',
		tilePassword: '',
		role: CompanyRole.CLIENT,
		status: CompanyStatus.ACTIVE,
		geofenceTelegramAlertsEnabled: false,
		telegramChatId: '',
	});
	const [cities, setCities] = useState<City[]>([]);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const handleClose = (value: boolean) => {
		setOpen(value);
		if (!value) {
			onClose();
		}
	};

	useEffect(() => {
		loadCities();
	}, []);

	useEffect(() => {
		if (company) {
			setFormData({
				name: company.name || '',
				email: company.email || '',
				phone: company.phone ?? '',
				taxId: company.taxId || '',
				city:
					typeof company.city === 'object' && company.city
						? company.city._id
						: company.city || '',
				tileAccount: company.tileAccount || '',
				tilePassword: '',
				role: company.role || CompanyRole.CLIENT,
				status: company.status || CompanyStatus.ACTIVE,
				geofenceTelegramAlertsEnabled:
					company.geofenceTelegramAlertsEnabled ?? false,
				telegramChatId: company.telegramChatId ?? '',
			});
		}
	}, [company]);

	const loadCities = async () => {
		try {
			const data = await fetchCities();
			setCities(Array.isArray(data) ? data : []);
		} catch (error: any) {
			setCities([]);
			toasterNotification({
				msg: error?.message || 'Error loading cities',
				toasterType: toasterOptions.error,
			});
		}
	};

	const handleChange = (field: keyof CompanyEditorForm, value: any) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (errors[field]) {
			setErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[field];
				return newErrors;
			});
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.name || formData.name.trim() === '') {
			newErrors.name = 'Name is required';
		}

		if (!formData.email || formData.email.trim() === '') {
			newErrors.email = 'Email is required';
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'Please enter a valid email address';
		}

		if (!formData.city || formData.city.trim() === '') {
			newErrors.city = 'City is required';
		}

		if (!formData.role) {
			newErrors.role = 'Role is required';
		}

		if (!formData.status) {
			newErrors.status = 'Status is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			toasterNotification({
				msg: 'Please fill in all required fields',
				toasterType: toasterOptions.error,
			});
			return;
		}

		setLoading(true);
		try {
			if (company) {
				const payload: UpdateCompanyDto = { ...formData };
				if (!payload.tilePassword?.trim()) {
					delete payload.tilePassword;
				}
				await updateCompany(company._id, payload);
				toasterNotification({
					msg: 'Company updated successfully',
					toasterType: toasterOptions.success,
				});
			} else {
				await createCompany(
					omit(formData, [
						'geofenceTelegramAlertsEnabled',
						'telegramChatId',
					]) as CreateCompanyDto,
				);
				toasterNotification({
					msg: 'Company created successfully',
					toasterType: toasterOptions.success,
				});
			}
			onSave();
		} catch (error: any) {
			const errorMessage = extractErrorMessage(error) || 'Error saving company';
			toasterNotification({
				msg: errorMessage,
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<SideModal open={open} setOpen={handleClose} onClose={onClose}>
			<div style={{ padding: '24px', minWidth: '500px' }}>
				<Typography
					scale="heading4"
					weight="600"
					style={{ marginBottom: '24px' }}
				>
					{company ? 'Edit Company' : 'Create Company'}
				</Typography>

				<Input
					name="name"
					type="text"
					title="Name"
					value={formData.name}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('name', e.target.value)
					}
					required
				/>
				{errors.name && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: '4px' }}
					>
						{errors.name}
					</Typography>
				)}

				<Input
					name="email"
					type="email"
					title="Email"
					value={formData.email}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('email', e.target.value)
					}
					required
				/>
				{errors.email && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: '4px' }}
					>
						{errors.email}
					</Typography>
				)}

				<Input
					name="phone"
					type="text"
					title="Phone"
					value={formData.phone || ''}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('phone', e.target.value)
					}
				/>

				<Input
					name="taxId"
					type="text"
					title="Tax ID"
					value={formData.taxId}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('taxId', e.target.value)
					}
					required
				/>

				<Input
					name="city"
					type="select"
					title="City"
					value={formData.city}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						handleChange('city', e.target.value)
					}
					options={cities.map((city) => ({
						value: city._id,
						label: city.name,
					}))}
				/>
				{errors.city && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: '4px' }}
					>
						{errors.city}
					</Typography>
				)}

				<Input
					name="tileAccount"
					type="text"
					title="Tile Account"
					value={formData.tileAccount}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('tileAccount', e.target.value)
					}
					required
				/>

				<Input
					name="tilePassword"
					type="password"
					title="Tile Password"
					value={formData.tilePassword}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleChange('tilePassword', e.target.value)
					}
					required={!company}
					placeholder={company ? 'Leave empty to keep current' : ''}
				/>

				<Input
					name="role"
					type="select"
					title="Role"
					value={formData.role}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						handleChange('role', e.target.value)
					}
					options={[
						{ value: CompanyRole.CLIENT, label: 'Client' },
						{ value: CompanyRole.ADMIN, label: 'Admin' },
					]}
				/>
				{errors.role && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: '4px' }}
					>
						{errors.role}
					</Typography>
				)}

				<Input
					name="status"
					type="select"
					title="Status"
					value={formData.status || CompanyStatus.ACTIVE}
					onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
						handleChange('status', e.target.value)
					}
					options={[
						{ value: CompanyStatus.ACTIVE, label: 'Active' },
						{ value: CompanyStatus.TRIAL, label: 'Trial' },
						{ value: CompanyStatus.CANCELLED, label: 'Cancelled' },
					]}
				/>
				{errors.status && (
					<Typography
						scale="small"
						weight="400"
						style={{ color: 'red', marginTop: '4px' }}
					>
						{errors.status}
					</Typography>
				)}

				{company ? (
					<>
						<Typography
							scale="medium"
							weight="600"
							style={{ marginTop: '20px', marginBottom: '12px' }}
						>
							Geocercas — alertas Telegram
						</Typography>
						<CheckboxInput
							className="m_b_md"
							name="geofenceTelegramAlertsEnabled"
							label="Enviar alertas de entrada y salida de geocerca por Telegram"
							checked={formData.geofenceTelegramAlertsEnabled}
							value="on"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								handleChange('geofenceTelegramAlertsEnabled', e.target.checked)
							}
						/>
						<Input
							name="telegramChatId"
							type="text"
							title="ID de chat de Telegram"
							value={formData.telegramChatId}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								handleChange('telegramChatId', e.target.value)
							}
							placeholder="-1001234567890"
						/>
						<Typography
							scale="xxsmall"
							weight="400"
							className="text_neutral_600"
							style={{ marginTop: '6px' }}
						>
							El backend debe tener TELEGRAM_BOT_TOKEN configurado. Use el ID
							numérico del chat, grupo o canal donde recibirá los avisos.
						</Typography>
					</>
				) : null}

				<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
					<Button variant="principal" onClick={handleSubmit} disabled={loading}>
						{loading ? 'Saving...' : 'Save'}
					</Button>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
				</div>
			</div>
		</SideModal>
	);
}

export default EditCompany;
